import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, ShieldCheck, Lock } from "lucide-react";
import { getAdminSetupStatus, claimFirstAdmin } from "@/lib/admin-setup.functions";

export const Route = createFileRoute("/_authenticated/setup-admin")({
  head: () => ({
    meta: [
      { title: "First-Admin Setup — Michael's Painting" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SetupAdminPage,
  errorComponent: ({ error }) => (
    <div className="py-32 text-center text-red-400">{error.message}</div>
  ),
  notFoundComponent: () => (
    <div className="py-32 text-center text-zinc-400">Not found.</div>
  ),
});

function SetupAdminPage() {
  const navigate = useNavigate();
  const getStatus = useServerFn(getAdminSetupStatus);
  const claim = useServerFn(claimFirstAdmin);

  const [loading, setLoading] = useState(true);
  const [adminExists, setAdminExists] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getStatus();
        if (active) setAdminExists(res.adminExists);
      } catch (e) {
        if (active) setError((e as Error).message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [getStatus]);

  const onClaim = async () => {
    setBusy(true); setError(null);
    try {
      await claim();
      navigate({ to: "/admin/estimates" });
    } catch (e) {
      setError((e as Error).message);
      // Re-check status — likely someone else just claimed it.
      try {
        const res = await getStatus();
        setAdminExists(res.adminExists);
      } catch { /* noop */ }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="min-h-screen bg-[#030303] text-white grid place-items-center px-6 py-24">
      <div className="w-full max-w-md border border-white/10 rounded-2xl bg-white/[0.02] p-8 backdrop-blur">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="size-6 text-amber-300" />
          <h1 className="font-serif text-3xl">First-Admin Setup</h1>
        </div>
        <p className="text-sm text-zinc-500 mb-6">
          One-time bootstrap. Available only while no admin exists.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-zinc-400 text-sm">
            <Loader2 className="size-4 animate-spin" /> Checking status…
          </div>
        )}

        {!loading && adminExists === true && (
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 text-sm text-zinc-300 flex items-start gap-3">
            <Lock className="size-4 mt-0.5 text-zinc-500" />
            <div>
              <div className="font-medium text-white">Admin already configured</div>
              <p className="text-zinc-500 mt-1">
                This setup page is permanently disabled. Contact an existing admin to be granted access.
              </p>
            </div>
          </div>
        )}

        {!loading && adminExists === false && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">
              You're signed in. Claiming will assign the <span className="text-amber-300">admin</span> role
              to your account and unlock the estimates dashboard.
            </p>
            <button
              onClick={onClaim}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-400 text-black font-medium py-3 hover:bg-amber-300 transition disabled:opacity-60"
            >
              {busy ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
              Make this account the first admin
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4 text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-lg p-3">
            {error}
          </div>
        )}
      </div>
    </section>
  );
}